import React from 'react';
import { useAuth } from '../components/AuthProvider';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { useNavigate } from 'react-router-dom';

const Home: React.FC = () => {
  const { user, signOut } = useAuth();
  const { language, setLanguage, isRTL } = useLanguage();
  const t = (key: string) => getTranslation(key as any, language);
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      {/* Navigation */}
      <nav className="bg-white/90 backdrop-blur-md shadow-soft" style={{borderBottom: '1px solid #05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${isRTL ? 'ml-3' : 'mr-3'} flex items-center justify-center shadow-lg overflow-hidden`} style={{backgroundColor: '#05472A'}}>
                <img 
                  src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-lg sm:text-xl font-bold" style={{color: '#05472A'}}>{t('tamuService')}</h1>
            </div>
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 sm:space-x-4`}>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setLanguage('en')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all duration-300 ${
                    language === 'en' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={language === 'en' ? {backgroundColor: '#05472A'} : {}}
                >
                  EN
                </button>
                <button 
                  onClick={() => setLanguage('ar')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all duration-300 ${
                    language === 'ar' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={language === 'ar' ? {backgroundColor: '#05472A'} : {}}
                >
                  AR
                </button>
              </div>
              <span className="hidden md:block text-sm font-medium" style={{color: '#05472A'}}>{t('hi')}, {user?.username}</span>
              <button 
                onClick={() => navigate('/orders')}
                className="bg-white hover:bg-green-50 border px-3 py-2 sm:px-4 sm:py-2 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium text-sm"
                style={{color: '#05472A', borderColor: '#05472A40'}}
              >
                {t('myOrders')}
              </button>
              <button onClick={signOut} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium" style={{color: '#05472A', borderColor: '#05472A40'}}>
                <span className="hidden sm:inline">{t('signOut')}</span>
                <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{color: '#05472A'}}>
            {t('welcomeMessage')}
          </h2>
          <p className="text-base sm:text-lg" style={{color: '#05472A99'}}>
            {t('chooseYourService')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Menu Food & Drink Card */}
          <div 
            onClick={() => navigate('/menu-order')}
            className="group relative overflow-hidden rounded-3xl shadow-strong hover:shadow-xl transition-all duration-500 cursor-pointer transform hover:scale-105 border-2"
            style={{borderColor: '#05472A20', minHeight: '400px'}}
          >
            <div className="absolute inset-0">
              <img 
                src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/Gemini_Generated_Image_92ybcc92ybcc92yb-1.png"
                alt="Menu Food & Drink"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            </div>
            
            <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
              <div className="mb-4">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg" style={{backgroundColor: '#05472A'}}>
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                {t('menuFoodDrinkSection')}
              </h3>
              
              <div className="flex items-center text-white font-semibold group-hover:gap-3 gap-2 transition-all duration-300">
                <span>{t('viewMenu')}</span>
                <svg className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </div>
          </div>

          {/* Guest Serving Product Card */}
          <div 
            onClick={() => navigate('/products')}
            className="group relative overflow-hidden rounded-3xl shadow-strong hover:shadow-xl transition-all duration-500 cursor-pointer transform hover:scale-105 border-2"
            style={{borderColor: '#05472A20', minHeight: '400px'}}
          >
            <div className="absolute inset-0">
              <img 
                src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/Gemini_Generated_Image_s47dims47dims47d-1.png"
                alt="Guest Serving Product"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
            </div>
            
            <div className="relative h-full flex flex-col justify-end p-6 sm:p-8">
              <div className="mb-4">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg" style={{backgroundColor: '#05472A'}}>
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
              </div>
              
              <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                {t('guestServingProductSection')}
              </h3>
              
              <div className="flex items-center text-white font-semibold group-hover:gap-3 gap-2 transition-all duration-300">
                <span>{t('viewProduct')}</span>
                <svg className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white/90 backdrop-blur-md border-t mt-12" style={{borderColor: '#05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center space-y-2">
            <p className="text-sm" style={{color: '#05472A99'}}>
              {t('copyright')}
            </p>
            <p className="text-sm font-semibold" style={{color: '#05472A'}}>
              {t('createdBy')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
